import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Globe, UserCheck, Trash2, Bell, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const lastUpdated = "December 15, 2024";
  const effectiveDate = "December 15, 2024";

  return (
    <div className="min-h-screen bg-[#0F0F1A]" data-testid="privacy-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-glass border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit']">Privacy Policy</h1>
              <p className="text-slate-400 text-sm">2A Valuence Mobile Application</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-8 pb-6 border-b border-white/10">
            <span>Last Updated: {lastUpdated}</span>
            <span>Effective Date: {effectiveDate}</span>
          </div>

          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Introduction */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                1. Introduction
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  Welcome to 2A Valuence ("we," "our," "us," or the "Company"). This Privacy Policy explains how Valuence Tech 
                  collects, uses, discloses, and safeguards your information when you use our mobile application 
                  "2A Valuence" (the "App"), available on iOS (Bundle ID: com.valuencetech.app).
                </p>
                <p>
                  By downloading, installing, or using the App, you agree to the collection and use of information in 
                  accordance with this Privacy Policy. If you do not agree with the terms of this Privacy Policy, 
                  please do not access or use the App.
                </p>
                <p className="text-sm text-slate-400 bg-white/5 p-3 rounded-lg">
                  <strong>Important:</strong> This Privacy Policy applies only to the 2A Valuence mobile application and 
                  does not apply to any third-party websites, services, or applications that may be linked from our App.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                2. Information We Collect
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <h3 className="text-lg font-semibold text-white">2.1 Information You Provide Directly</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Account Information:</strong> Name, email address, phone number, business name, and business address when you create an account.</li>
                  <li><strong>Profile Information:</strong> Business type, role, preferences, and settings you configure within the App.</li>
                  <li><strong>Device Pricing Data:</strong> Information about devices you search for or price, including make, model, storage capacity, condition, carrier status, and color.</li>
                  <li><strong>Customer Data:</strong> If you use our CRM features, customer names, contact information, device information, repair history, and transaction records you enter.</li>
                  <li><strong>Repair Ticket Data:</strong> Device information, issue descriptions, repair notes, parts used, technician assignments, and status updates.</li>
                  <li><strong>Digital Signatures:</strong> Electronic signatures captured through our digital waiver feature.</li>
                  <li><strong>Communication Data:</strong> Messages, support requests, and feedback you send to us.</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-6">2.2 Information Collected Automatically</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Device Information:</strong> Device type, operating system version, unique device identifiers, IP address, and mobile network information.</li>
                  <li><strong>Usage Data:</strong> Features accessed, search queries, time spent on screens, click patterns, and navigation paths.</li>
                  <li><strong>Log Data:</strong> Access times, pages viewed, app crashes, and other system activity.</li>
                  <li><strong>Location Data:</strong> General geographic location based on IP address (we do not collect precise GPS location).</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-6">2.3 Information from Third Parties</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Pricing Data:</strong> Market pricing information from public sources including eBay, Swappa, Back Market, Amazon, and other resale platforms.</li>
                  <li><strong>Email Service Providers:</strong> If you connect Gmail or Outlook, we access only the permissions necessary to send repair notification emails on your behalf.</li>
                  <li><strong>Payment Processors:</strong> If you subscribe to premium features, payment information is processed by third-party payment processors; we do not store full credit card numbers.</li>
                </ul>
              </div>
            </section>

            {/* How We Use Information */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                3. How We Use Your Information
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>We use the information we collect for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Service Delivery:</strong> To provide, maintain, and improve the App's functionality, including pricing estimates, repair tracking, and CRM features.</li>
                  <li><strong>Personalization:</strong> To customize your experience, remember preferences, and provide relevant pricing recommendations.</li>
                  <li><strong>Communication:</strong> To send service-related notifications, respond to inquiries, and provide customer support.</li>
                  <li><strong>Analytics:</strong> To understand usage patterns, improve our algorithms, and develop new features.</li>
                  <li><strong>Security:</strong> To detect, prevent, and address fraud, unauthorized access, and other illegal activities.</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
                  <li><strong>Business Operations:</strong> To manage our business, including billing, account management, and enforcement of our terms.</li>
                </ul>

                <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg mt-4">
                  <p className="text-sm">
                    <strong className="text-primary">Note on Pricing Algorithms:</strong> Our pricing algorithms analyze 
                    aggregated, anonymized market data to provide estimates. Individual user searches are not shared 
                    with third parties for their marketing purposes.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                4. How We Share Your Information
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p><strong>We do not sell your personal information.</strong> We may share information in the following circumstances:</p>
                
                <h3 className="text-lg font-semibold text-white">4.1 Service Providers</h3>
                <p>We may share information with third-party vendors who perform services on our behalf, including:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Cloud hosting and data storage providers</li>
                  <li>Analytics and crash reporting services</li>
                  <li>Email delivery services</li>
                  <li>Payment processing services</li>
                  <li>Customer support platforms</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-4">4.2 Legal Requirements</h3>
                <p>We may disclose information if required by law or in response to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Subpoenas, court orders, or legal processes</li>
                  <li>Requests from law enforcement or government agencies</li>
                  <li>To protect our rights, property, or safety, or that of our users or others</li>
                  <li>To investigate potential violations of our Terms of Service</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-4">4.3 Business Transfers</h3>
                <p>
                  If we are involved in a merger, acquisition, or sale of assets, your information may be transferred 
                  as part of that transaction. We will notify you of any change in ownership or control of your personal information.
                </p>

                <h3 className="text-lg font-semibold text-white mt-4">4.4 Aggregated Data</h3>
                <p>
                  We may share aggregated, anonymized data that cannot reasonably be used to identify you for 
                  industry analysis, market research, and similar purposes.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                5. Data Security
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>We implement industry-standard security measures to protect your information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Encryption:</strong> All data is encrypted in transit using TLS/SSL and at rest using AES-256 encryption.</li>
                  <li><strong>Access Controls:</strong> Access to personal data is restricted to authorized personnel on a need-to-know basis.</li>
                  <li><strong>Infrastructure:</strong> We use secure cloud infrastructure with regular security audits and penetration testing.</li>
                  <li><strong>Monitoring:</strong> We continuously monitor our systems for suspicious activity and potential vulnerabilities.</li>
                  <li><strong>Authentication:</strong> We implement secure authentication mechanisms including password hashing and session management.</li>
                </ul>
                <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-lg mt-4">
                  <p className="text-sm text-yellow-200">
                    <strong>Disclaimer:</strong> While we strive to protect your information, no method of transmission 
                    over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Retention */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                6. Data Retention
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>We retain your information for as long as necessary to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide you with the services you requested</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce our agreements</li>
                  <li>Maintain business records as required by law</li>
                </ul>
                <p className="mt-4">
                  <strong>Account Data:</strong> Retained while your account is active and for up to 3 years after account deletion for legal compliance.
                </p>
                <p>
                  <strong>Usage Data:</strong> Retained for up to 2 years for analytics purposes, then aggregated or deleted.
                </p>
                <p>
                  <strong>Customer/Repair Data:</strong> Retained as long as your account is active. Upon account deletion, this data is deleted within 30 days unless legal retention is required.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" />
                7. Your Rights and Choices
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>Depending on your location, you may have the following rights:</p>
                
                <h3 className="text-lg font-semibold text-white">7.1 Access and Portability</h3>
                <p>You can request a copy of the personal data we hold about you in a portable format.</p>

                <h3 className="text-lg font-semibold text-white mt-4">7.2 Correction</h3>
                <p>You can request correction of inaccurate or incomplete personal data.</p>

                <h3 className="text-lg font-semibold text-white mt-4">7.3 Deletion</h3>
                <p>You can request deletion of your personal data, subject to legal retention requirements.</p>

                <h3 className="text-lg font-semibold text-white mt-4">7.4 Restriction and Objection</h3>
                <p>You can request restriction of processing or object to processing in certain circumstances.</p>

                <h3 className="text-lg font-semibold text-white mt-4">7.5 Withdraw Consent</h3>
                <p>Where processing is based on consent, you can withdraw consent at any time.</p>

                <h3 className="text-lg font-semibold text-white mt-4">7.6 How to Exercise Your Rights</h3>
                <p>To exercise any of these rights, contact us at:</p>
                <div className="bg-white/5 p-4 rounded-lg mt-2">
                  <p><strong>Email:</strong> 2avaluence@gmail.com</p>
                  <p><strong>Subject Line:</strong> Privacy Rights Request</p>
                </div>
                <p className="text-sm text-slate-400 mt-2">
                  We will respond to your request within 30 days. We may require verification of your identity before processing requests.
                </p>
              </div>
            </section>

            {/* California Residents */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">8. California Privacy Rights (CCPA)</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Right to Know:</strong> You can request information about the categories and specific pieces of personal information we collect.</li>
                  <li><strong>Right to Delete:</strong> You can request deletion of your personal information.</li>
                  <li><strong>Right to Opt-Out:</strong> You can opt out of the "sale" of personal information. Note: We do not sell personal information.</li>
                  <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
                </ul>
                <p className="mt-4">
                  To make a CCPA request, email us at <strong>2avaluence@gmail.com</strong> with the subject line "CCPA Request."
                </p>
              </div>
            </section>

            {/* International Users */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">9. International Data Transfers</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  Our services are primarily operated in the United States. If you access the App from outside the 
                  United States, your information may be transferred to, stored, and processed in the United States 
                  or other countries where our service providers operate.
                </p>
                <p>
                  By using the App, you consent to the transfer of your information to countries that may have 
                  different data protection laws than your country of residence.
                </p>
                <p>
                  For users in the European Economic Area (EEA), we rely on appropriate safeguards such as Standard 
                  Contractual Clauses for data transfers.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">10. Children's Privacy</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  The App is not intended for use by children under the age of 13 (or 16 in the EEA). We do not 
                  knowingly collect personal information from children under these ages.
                </p>
                <p>
                  If we become aware that we have collected personal information from a child under the applicable 
                  age, we will take steps to delete that information promptly.
                </p>
                <p>
                  If you believe we have collected information from a child, please contact us immediately at 
                  <strong> 2avaluence@gmail.com</strong>.
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4">11. Third-Party Services and Links</h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  The App may contain links to third-party websites or integrate with third-party services. 
                  This Privacy Policy does not apply to those third parties.
                </p>
                <p>We encourage you to review the privacy policies of any third-party services you access, including:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>eBay (for pricing data)</li>
                  <li>Google (for Gmail integration)</li>
                  <li>Microsoft (for Outlook integration)</li>
                  <li>Apple (App Store and related services)</li>
                  <li>Payment processors</li>
                </ul>
                <p className="text-sm text-slate-400 mt-4">
                  We are not responsible for the privacy practices of third-party services.
                </p>
              </div>
            </section>

            {/* Changes to Policy */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                12. Changes to This Privacy Policy
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, 
                  technology, legal requirements, or other factors.
                </p>
                <p>When we make material changes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We will update the "Last Updated" date at the top of this policy</li>
                  <li>We will notify you via email or in-app notification</li>
                  <li>We may request your consent for material changes where required by law</li>
                </ul>
                <p className="mt-4">
                  Your continued use of the App after changes become effective constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="glass-card p-5 sm:p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                13. Contact Us
              </h2>
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                <div className="bg-white/5 p-4 rounded-lg space-y-2">
                  <p><strong className="text-white">2A Valuence / Valuence Tech</strong></p>
                  <p><strong>Email:</strong> 2avaluence@gmail.com</p>
                  <p><strong>Website:</strong> https://2avaluence.com</p>
                  <p><strong>App Store:</strong> https://apps.apple.com/app/id6759179016</p>
                </div>
                <p className="text-sm text-slate-400 mt-4">
                  We aim to respond to all inquiries within 5 business days.
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="glass-card p-5 sm:p-6 border-2 border-primary/30">
              <h2 className="text-xl font-bold text-white mb-4">14. Acknowledgment</h2>
              <div className="text-slate-300 leading-relaxed">
                <p>
                  By using the 2A Valuence App, you acknowledge that you have read and understood this Privacy Policy 
                  and agree to the collection, use, and disclosure of your information as described herein.
                </p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} 2A Valuence. All rights reserved.
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/terms" className="text-primary hover:underline text-sm">Terms of Service</Link>
              <Link to="/support" className="text-primary hover:underline text-sm">Support</Link>
              <Link to="/" className="text-primary hover:underline text-sm">Home</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
